import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  clearRouteHistory,
  getLastQuery,
  getRecentQueries,
  pushRecent,
  setLastQuery,
} from '../utils/listQuery/history'

/**
 * URL-as-source-of-truth list filters with session last-query restore + recent history.
 *
 * Mount priority:
 * 1. Explicit URL params → use URL
 * 2. Else session last-query → restore into URL (replace)
 * 3. Else parse(empty) defaults
 */
export function useListQueryState({
  routeKey,
  parse,
  build,
  hasExplicitParams,
  labelFromFilters = () => 'Filters',
  isDefaultFilters = () => false,
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [recentTick, setRecentTick] = useState(0)

  const [bootstrapFilters] = useState(() => {
    if (!hasExplicitParams(searchParams)) {
      return getLastQuery(routeKey)
    }
    return null
  })

  useEffect(() => {
    if (!bootstrapFilters) return
    if (hasExplicitParams(searchParams)) return
    setSearchParams(build(bootstrapFilters), { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restore once on mount
  }, [])

  const filters = useMemo(() => {
    if (!hasExplicitParams(searchParams) && bootstrapFilters) {
      return bootstrapFilters
    }
    return parse(searchParams)
  }, [bootstrapFilters, hasExplicitParams, parse, searchParams])

  const setFilters = useCallback(
    (patch, { commitRecent = false } = {}) => {
      const next =
        typeof patch === 'function' ? patch(filters) : { ...filters, ...patch }
      setSearchParams(build(next), { replace: true })
      setLastQuery(routeKey, next)
      if (commitRecent && !isDefaultFilters(next)) {
        pushRecent(routeKey, next, labelFromFilters(next))
        setRecentTick((t) => t + 1)
      }
    },
    [
      build,
      filters,
      isDefaultFilters,
      labelFromFilters,
      routeKey,
      setSearchParams,
    ],
  )

  const resetFilters = useCallback(() => {
    const empty = parse(new URLSearchParams())
    clearRouteHistory(routeKey)
    setSearchParams(build(empty), { replace: true })
    setRecentTick((t) => t + 1)
  }, [build, parse, routeKey, setSearchParams])

  const applyRecent = useCallback(
    (entryFilters) => {
      if (!entryFilters) return
      setSearchParams(build(entryFilters), { replace: true })
      setLastQuery(routeKey, entryFilters)
    },
    [build, routeKey, setSearchParams],
  )

  const recent = useMemo(() => {
    void recentTick
    return getRecentQueries(routeKey)
  }, [recentTick, routeKey])

  const isDefault = isDefaultFilters(filters)

  useEffect(() => {
    if (hasExplicitParams(searchParams)) {
      setLastQuery(routeKey, parse(searchParams))
    }
  }, [hasExplicitParams, parse, routeKey, searchParams])

  return {
    filters,
    searchParams,
    setFilters,
    resetFilters,
    applyRecent,
    recent,
    isDefault,
  }
}
